import { useEffect, useMemo, useRef, useState } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip
} from "chart.js";
import { Bar } from "react-chartjs-2";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { useTheme } from "../theme/ThemeContext";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, ChartDataLabels);

const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const readDateValue = (value) => {
  if (value === null || value === undefined || value === "") return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === "number") {
    const millis = value < 1e12 ? value * 1000 : value;
    const date = new Date(millis);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  if (typeof value === "string") {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  if (typeof value === "object") {
    return readDateValue(value.$date || value.date || value.value);
  }
  return null;
};

const readObjectIdDate = (id) => {
  const value = String(id || "");
  if (!/^[a-f0-9]{24}$/i.test(value)) return null;
  const seconds = parseInt(value.slice(0, 8), 16);
  if (!Number.isFinite(seconds)) return null;
  const date = new Date(seconds * 1000);
  return Number.isNaN(date.getTime()) ? null : date;
};

const readClientCreatedDate = (client) => (
  readDateValue(client?.createdDate ?? client?.createdAt ?? client?.created_at)
  || readObjectIdDate(client?.id)
);

function ClientsCreatedChart({ clients, sourceLabel, loading = false }) {
  const { theme } = useTheme();
  const isLightTheme = theme === "light";
  const labelColor = isLightTheme ? "#111111" : "#ffffff";
  const popupRef = useRef(null);
  const [activeBar, setActiveBar] = useState(null);
  const createdClients = useMemo(() => (
    Array.isArray(clients)
      ? clients
        .map((client) => ({
          name: client?.name || "Unknown Client",
          date: readClientCreatedDate(client)
        }))
        .filter((client) => client.date)
      : []
  ), [clients]);

  const yearCounts = useMemo(() => {
    const counts = new Map();
    createdClients.forEach(({ date }) => {
      const year = date.getFullYear();
      const months = counts.get(year) || Array(12).fill(0);
      months[date.getMonth()] += 1;
      counts.set(year, months);
    });
    return counts;
  }, [createdClients]);

  const clientsByMonth = useMemo(() => {
    const grouped = new Map();
    createdClients.forEach(({ name, date }) => {
      const key = `${date.getFullYear()}-${date.getMonth()}`;
      const names = grouped.get(key) || [];
      names.push(name);
      grouped.set(key, names);
    });
    grouped.forEach((names) => names.sort((a, b) => a.localeCompare(b)));
    return grouped;
  }, [createdClients]);

  const years = useMemo(
    () => Array.from(yearCounts.keys()).sort((a, b) => b - a),
    [yearCounts]
  );

  const [selectedYear, setSelectedYear] = useState(null);
  const activeYear = years.includes(selectedYear) ? selectedYear : (years[0] ?? null);
  const monthCounts = activeYear ? (yearCounts.get(activeYear) || Array(12).fill(0)) : Array(12).fill(0);
  const yearTotal = monthCounts.reduce((sum, count) => sum + count, 0);
  const missingCount = Math.max((clients?.length || 0) - createdClients.length, 0);
  const activeNames = activeBar && activeYear !== null
    ? (clientsByMonth.get(`${activeYear}-${activeBar.monthIndex}`) || [])
    : [];

  useEffect(() => {
    setActiveBar(null);
  }, [activeYear, clients]);

  useEffect(() => {
    if (!activeBar) return undefined;

    const closeOnOutside = (event) => {
      if (popupRef.current?.contains(event.target)) return;
      if (event.target?.tagName === "CANVAS") return;
      setActiveBar(null);
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setActiveBar(null);
    };

    document.addEventListener("pointerdown", closeOnOutside);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOnOutside);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [activeBar]);

  const chartData = useMemo(() => ({
    labels: MONTH_LABELS,
    datasets: [
      {
        label: "Clients created",
        data: monthCounts,
        backgroundColor: "#3b82f6",
        borderRadius: 6,
        maxBarThickness: 42
      }
    ]
  }), [monthCounts]);

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    layout: { padding: { top: 22 } },
    onHover: (event, elements) => {
      const canvas = event?.native?.target;
      if (canvas) canvas.style.cursor = elements?.length ? "pointer" : "default";
    },
    onClick: (event, elements, chart) => {
      const hit = elements?.[0];
      if (!hit || !chart) {
        setActiveBar(null);
        return;
      }
      const monthIndex = hit.index;
      const names = clientsByMonth.get(`${activeYear}-${monthIndex}`) || [];
      if (names.length === 0) {
        setActiveBar(null);
        return;
      }
      const bar = chart.getDatasetMeta(0).data[monthIndex];
      const point = bar?.getProps?.(["x", "y"], true) || { x: bar?.x, y: bar?.y };
      if (point.x === undefined || point.y === undefined) return;
      const half = 130;
      const x = Math.min(Math.max(point.x, half), Math.max(chart.width - half, half));
      setActiveBar((current) => (
        current?.monthIndex === monthIndex ? null : { monthIndex, x, y: point.y }
      ));
    },
    plugins: {
      legend: { display: false },
      tooltip: { enabled: false },
      datalabels: {
        display: true,
        clip: false,
        color: labelColor,
        anchor: "end",
        align: "top",
        font: { weight: "700", size: 13 },
        formatter: (value) => Number(value) || 0
      }
    },
    scales: {
      x: {
        ticks: { color: labelColor },
        grid: { display: false }
      },
      y: {
        beginAtZero: true,
        grace: "18%",
        ticks: {
          color: labelColor,
          precision: 0
        },
        grid: { color: isLightTheme ? "rgba(17, 17, 17, 0.12)" : "rgba(255, 255, 255, 0.16)" }
      }
    }
  }), [activeYear, clientsByMonth, isLightTheme, labelColor]);

  return (
    <section id="clients-created" className="clients-created-chart" aria-label="Clients created by month">
      <div className="clients-created-chart-header">
        <div>
          <h2>Clients Created by Month</h2>
          <p>
            {sourceLabel} clients, grouped by the month of the date each client was created.
          </p>
        </div>
        {years.length > 0 && (
          <label className="clients-created-chart-year">
            Year
            <select
              value={activeYear || ""}
              onChange={(event) => setSelectedYear(Number(event.target.value))}
            >
              {years.map((year) => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </label>
        )}
      </div>

      {loading ? (
        <div className="clients-created-chart-empty">Loading client dates...</div>
      ) : years.length === 0 ? (
        <div className="clients-created-chart-empty">No created dates found for these clients.</div>
      ) : (
        <>
          <div className="clients-created-chart-canvas">
            <Bar data={chartData} options={chartOptions} />
            {activeBar && (
              <div
                ref={popupRef}
                className="clients-created-chart-popup"
                role="dialog"
                aria-label={`${MONTH_LABELS[activeBar.monthIndex]} ${activeYear} clients`}
                style={{ left: activeBar.x, top: activeBar.y }}
              >
                <div className="clients-created-chart-popup-header">
                  <strong>{MONTH_LABELS[activeBar.monthIndex]} {activeYear}</strong>
                  <button type="button" aria-label="Close" onClick={() => setActiveBar(null)}>×</button>
                </div>
                <p>{activeNames.length} client{activeNames.length === 1 ? "" : "s"}</p>
                <ul className="clients-created-chart-popup-list">
                  {activeNames.map((name, index) => (
                    <li key={`${name}-${index}`}>{name}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          <p className="clients-created-chart-total">
            {yearTotal} client{yearTotal === 1 ? "" : "s"} created in {activeYear}
            {missingCount > 0 ? ` · ${missingCount} without a created date` : ""}
          </p>
        </>
      )}
    </section>
  );
}

export default ClientsCreatedChart;
